import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Platform } from '@app/classes';
import { BulkSelectEmployeeDialogComponent, ConfirmationDialogComponent } from '@app/components';
import { Columns, Row } from '@app/components/simple-table/simple-table.component';
import { SHORT_DATE_FORMAT } from '@app/constants';
import { modelsNotInModels } from '@app/functions';
import { QueryFetcher } from '@app/models/core/query-fetcher.model';
import { Employee } from '@app/models/employee/employee.model';
import { Project } from '@app/models/time-tracking/project.model';
import { TimesheetEntry } from '@app/models/time-tracking/timesheet-entry.model';
import moment from 'moment';
import { assignEmployeesProjectTableColumns } from '../../meta/assign-employees-project-table.columns';

const mapEmployeesToRow = (employee: Employee): Row => {
    return {
        cells: [{ employee, isAvatar: true }, moment().format(SHORT_DATE_FORMAT[employee.locale])],
    };
};
@Component({
    selector: 'app-assign-employees-project-table',
    templateUrl: './assign-employees-project-table.template.html',
    styleUrls: ['./assign-employees-project-table.style.scss'],
})
export class AssignEmployeesProjectTable implements OnInit {
    @ViewChild('selectEmployees', { static: true }) selectEmployees!: BulkSelectEmployeeDialogComponent;
    @ViewChild('confirmRemoveModal') confirmRemoveModal!: ConfirmationDialogComponent;

    @Input() employees: Employee[] = [];
    @Input() project: Project | null = null;

    /**
     * Allow modification of the employees
     * Can be disabled if the user is not allowed to modify the employees, for whatever reason
     */
    @Input() allowModification = true;

    @Output() onEmployeesChange: EventEmitter<Employee[]> = new EventEmitter<Employee[]>();
    @Output() onRemoveChange: EventEmitter<Employee> = new EventEmitter<Employee>();

    rows: Row[] = [];
    isLoading = false;

    columns: Columns[] = assignEmployeesProjectTableColumns;

    /**
     * employeesPendingRemoval is used by the confirmation dialog to show the employees that will be removed
     */
    employeesPendingRemoval: Employee[] = [];

    /**
     * employeesPendingRemovalWithPendingHours is used by the confirmation dialog to show the employees that will be removed
     * and have pending hours
     */
    employeesPendingRemovalWithPendingHours: Employee[] = [];

    /**
     * removeOrConfirmLabel is used by the confirmation dialog to show the correct label
     * depending on whether the user is removing or confirming the removal of employees
     */
    removeOrConfirmLabel: 'time-tracking.remove' | 'time-tracking.confirm' = 'time-tracking.remove';

    ngOnInit(): void {
        this.mapEmployeesToRows();
    }

    ngOnChanges(): void {
        this.calculateHasPendingHours();
    }

    onSearch(searchQuery: string | null): void {
        if (searchQuery === null) {
            this.mapEmployeesToRows();
            return;
        }

        const nameIncludesSearchQuery = (employee: Employee): boolean => {
            if (employee.fullName) {
                return employee.fullName.toLowerCase().includes(searchQuery.toLocaleLowerCase());
            }

            return false;
        };

        this.rows = this.employees.filter(nameIncludesSearchQuery).map(mapEmployeesToRow);
    }

    async openEmployeeSelect(): Promise<void> {
        const employeesWhoWillBeOnProject = await this.selectEmployees.show<Employee[]>(this.employees);

        if (employeesWhoWillBeOnProject === null) {
            return;
        }

        const employeesToDetach: Employee[] = modelsNotInModels(this.employees, employeesWhoWillBeOnProject);

        this.removeOrConfirmLabel = 'time-tracking.confirm';
        const confirmRemoval = await this.confirmRemoval(employeesToDetach);
        if (!confirmRemoval) {
            // yes, this is the intended logic.
            // if you don't confirm removal, we go back to employee select
            this.openEmployeeSelect();
            return;
        }

        this.employees = employeesWhoWillBeOnProject;
        this.mapEmployeesToRows();

        this.onEmployeesChange.emit(this.employees);
    }

    async removeEmployee(index: number): Promise<void> {
        const employeeToRemove = this.employees[index];

        this.removeOrConfirmLabel = 'time-tracking.remove';
        const confirmRemoval = await this.confirmRemoval([employeeToRemove]);
        if (!confirmRemoval) {
            return;
        }

        this.onRemoveChange.emit(employeeToRemove);

        this.employees = this.employees.filter((e) => !e.is(employeeToRemove));

        this.mapEmployeesToRows();
    }

    whereOnTimeTracking(query: QueryFetcher): QueryFetcher {
        return query.where('isAssignedModule', Platform.modules.timeTracking);
    }

    tooltipTextMaker(employee: Employee): string | null {
        if (employee.hasPendingTimeTrackingHours) {
            return 'time-tracking.employeeHasPendingHoursProject';
        }

        return null;
    }

    /**
     * calculateHasPendingHours flags any employee that has pending hours
     * on this project. We use these values in the UI.
     */
    private async calculateHasPendingHours(): Promise<void> {
        if (!this.project) {
            return;
        }

        const employeeIds = this.employees.map((e) => e.id);

        const [timesheets] = await TimesheetEntry.where('project_id', this.project.id)
            .where('status', ['pending'])
            .whereIn('employee_id', employeeIds)
            .all();

        const pendingHoursEmployeeIds = new Set<number>(timesheets.map((t) => t.employeeId));

        this.employees.forEach((emp) => {
            if (pendingHoursEmployeeIds.has(emp.id)) {
                emp.hasPendingTimeTrackingHours = true;
            }
        });
    }

    /**
     * confirmRemoval shows a confirmation dialog for the removal of employees
     * it also sets so instance variables needed for the ui
     */
    private async confirmRemoval(employeesToRemove: Employee[]): Promise<boolean> {
        // on a create view, we don't need to confirm removal
        if (!this.project?.isPersisted) {
            return true;
        }

        if (!employeesToRemove.length) {
            return true;
        }

        this.employeesPendingRemoval = employeesToRemove;
        this.employeesPendingRemovalWithPendingHours = employeesToRemove.filter((e) => e.hasPendingTimeTrackingHours);
        const shouldRemove = await this.confirmRemoveModal.open();

        return shouldRemove;
    }

    /**
     * mapEmployeesToRows sets the rows in the tables based on the current employees
     * Any time you want to update the table based on new employee information, call this function!
     */
    private mapEmployeesToRows(): void {
        this.rows = this.employees.map(mapEmployeesToRow);
    }
}
